import React, { useState } from 'react';
import { FiPrinter, FiSave } from 'react-icons/fi';

const CreditApplicationForm = ({ dealData = {} }) => {
  const [formData, setFormData] = useState({
    // Part I - Applicant Information
    lastName: dealData?.customer_name?.split(' ').slice(-1)[0] || '',
    firstName: dealData?.customer_name?.split(' ')[0] || '',
    middleInitial: '',
    homePhone: dealData?.customer_phone || '',
    cellPhone: '',
    dateOfBirth: '',
    ssn: '',
    driversLicense: '',
    email: dealData?.customer_email || '',
    
    // Address
    address: '',
    suite: '',
    zip: '',
    city: '',
    state: '',
    howLong: '',
    
    // Prior Address
    priorAddress: '',
    priorCity: '',
    priorState: '',
    priorZip: '',
    priorHowLong: '',
    
    // Employment
    presentEmployer: '',
    employerAddress: '',
    employerCity: '',
    employerState: '',
    employerZip: '',
    employerPhone: '',
    position: '',
    employmentLength: '',
    
    // Previous Employment
    previousEmployer: '',
    prevEmployerAddress: '',
    prevEmployerCity: '',
    prevEmployerState: '',
    prevEmployerZip: '',
    prevEmployerPhone: '',
    prevPosition: '',
    prevEmploymentLength: '',
    
    // Income
    grossMonthlyIncome: '',
    additionalIncome: '',
    additionalIncomeSource: '',
    totalMonthlyIncome: '',
    
    // Part II - Co-Applicant (similar fields)
    coApplicant: false,
    
    // Part III - Assets and Debts
    debts: [],
    
    // Part IV - Insurance
    insuranceCompany: '',
    insuranceAgent: '',
    insurancePhone: '',
    
    // Vehicle
    vehicleYear: dealData?.vehicles_db2025?.year || '',
    vehicleMake: dealData?.vehicles_db2025?.make || '',
    vehicleModel: dealData?.vehicles_db2025?.model || '',
    vehicleVin: dealData?.vehicles_db2025?.vin || '',
    vehiclePrice: dealData?.vehicle_price || '',
    downPayment: dealData?.down_payment || '',
    
    // Signatures
    applicantSignature: '',
    coApplicantSignature: '',
    signatureDate: new Date().toLocaleDateString(),
  });

  const handlePrint = () => {
    window.print();
  };

  const handleSave = () => {
    // Save logic here
    console.log('Saving form data:', formData);
  };

  return (
    <div className="bg-white max-w-6xl mx-auto p-4 credit-application-form">
      <style jsx>{`
        @media print {
          .no-print { display: none !important; }
          .credit-application-form { padding: 0 !important; }
          input, select { 
            border: none !important; 
            padding: 0 !important;
            font-size: 11px !important;
          }
        }
      `}</style>

      {/* Action Buttons */}
      <div className="no-print flex justify-end space-x-2 mb-4">
        <button
          onClick={handlePrint}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-2"
        >
          <FiPrinter className="w-4 h-4" />
          <span>Print</span>
        </button>
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center space-x-2"
        >
          <FiSave className="w-4 h-4" />
          <span>Save</span>
        </button>
      </div>

      {/* Header */}
      <div className="bg-black text-white text-center py-2 mb-4">
        <h1 className="text-lg font-bold">PURCHASE OR LEASE CREDIT APPLICATION</h1>
        <p className="text-xs">IMPORTANT PRIVACY NOTICE: THIS NOTICE IS READ TO YOU BY DEALERSHIP EMPLOYEE</p>
      </div>

      {/* Directions */}
      <div className="border border-gray-400 p-2 mb-4 text-xs">
        <p className="font-bold">Directions: Check the appropriate box.</p>
        <div className="flex gap-4 mt-1">
          <label>
            <input type="checkbox" className="mr-1" />
            If you are applying for individual credit in your name, and you are relying on your own income or assets and not the income or assets of another person as the basis for repayment of the credit requested, complete only Parts I and III
          </label>
        </div>
        <div className="flex gap-4 mt-1">
          <label>
            <input type="checkbox" className="mr-1" />
            If you are applying for joint credit with another person, complete ALL Parts, and each applicant must sign
          </label>
        </div>
        <p className="mt-2">We intend to apply for joint credit: Applicant Initials ____ Co-Applicant Initials ____</p>
      </div>

      {/* Seller Info */}
      <div className="mb-4">
        <div className="bg-gray-800 text-white px-2 py-1 text-xs font-bold">SELLER:</div>
        <div className="p-2">
          <p className="font-bold">Paradise-Performance</p>
          <p className="text-sm">817-382-2316 | Fort Worth, TX</p>
        </div>
      </div>

      {/* PART I - APPLICANT INFORMATION */}
      <div className="mb-4">
        <div className="bg-gray-800 text-white px-2 py-1 text-xs font-bold mb-2">
          PART I. APPLICANT INFORMATION
        </div>
        
        <table className="w-full border-collapse text-xs">
          <tbody>
            <tr>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">LAST NAME:</span>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="ml-1 w-32 border-b border-gray-400"
                  />
                </label>
              </td>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">FIRST NAME:</span>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="ml-1 w-32 border-b border-gray-400"
                  />
                </label>
              </td>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">MIDDLE INITIAL:</span>
                  <input
                    type="text"
                    value={formData.middleInitial}
                    onChange={(e) => setFormData({...formData, middleInitial: e.target.value})}
                    className="ml-1 w-8 border-b border-gray-400"
                    maxLength="1"
                  />
                </label>
              </td>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">HOME PHONE:</span>
                  <input
                    type="tel"
                    value={formData.homePhone}
                    onChange={(e) => setFormData({...formData, homePhone: e.target.value})}
                    className="ml-1 w-24 border-b border-gray-400"
                  />
                </label>
              </td>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">CELL PHONE:</span>
                  <input
                    type="tel"
                    value={formData.cellPhone}
                    onChange={(e) => setFormData({...formData, cellPhone: e.target.value})}
                    className="ml-1 w-24 border-b border-gray-400"
                  />
                </label>
              </td>
            </tr>
            <tr>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">DATE OF BIRTH:</span>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                    className="ml-1 w-24 border-b border-gray-400"
                  />
                </label>
              </td>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">SOCIAL SECURITY NUMBER:</span>
                  <input
                    type="text"
                    value={formData.ssn}
                    onChange={(e) => setFormData({...formData, ssn: e.target.value})}
                    className="ml-1 w-24 border-b border-gray-400"
                    placeholder="XXX-XX-XXXX"
                  />
                </label>
              </td>
              <td className="border border-gray-400 p-1" colSpan="2">
                <label className="block">
                  <span className="font-semibold">DRIVER'S LICENSE NUMBER:</span>
                  <input
                    type="text"
                    value={formData.driversLicense}
                    onChange={(e) => setFormData({...formData, driversLicense: e.target.value})}
                    className="ml-1 w-32 border-b border-gray-400"
                  />
                </label>
              </td>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">EMAIL ADDRESS:</span>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="ml-1 w-32 border-b border-gray-400"
                  />
                </label>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Address Section */}
        <table className="w-full border-collapse text-xs mt-2">
          <tbody>
            <tr>
              <td className="border border-gray-400 p-1" colSpan="3">
                <label className="block">
                  <span className="font-semibold">ADDRESS:</span>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="ml-1 w-64 border-b border-gray-400"
                  />
                </label>
              </td>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">SUITE:</span>
                  <input
                    type="text"
                    value={formData.suite}
                    onChange={(e) => setFormData({...formData, suite: e.target.value})}
                    className="ml-1 w-12 border-b border-gray-400"
                  />
                </label>
              </td>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">ZIP:</span>
                  <input
                    type="text"
                    value={formData.zip}
                    onChange={(e) => setFormData({...formData, zip: e.target.value})}
                    className="ml-1 w-16 border-b border-gray-400"
                  />
                </label>
              </td>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">HOW LONG?</span>
                  <input
                    type="text"
                    value={formData.howLong}
                    onChange={(e) => setFormData({...formData, howLong: e.target.value})}
                    className="ml-1 w-16 border-b border-gray-400"
                    placeholder="Yrs/Mos"
                  />
                </label>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Employment Section */}
        <div className="mt-4">
          <h3 className="font-bold text-xs mb-2">PRESENT EMPLOYMENT:</h3>
          <table className="w-full border-collapse text-xs">
            <tbody>
              <tr>
                <td className="border border-gray-400 p-1" colSpan="2">
                  <label className="block">
                    <span className="font-semibold">EMPLOYER:</span>
                    <input
                      type="text"
                      value={formData.presentEmployer}
                      onChange={(e) => setFormData({...formData, presentEmployer: e.target.value})}
                      className="ml-1 w-48 border-b border-gray-400"
                    />
                  </label>
                </td>
                <td className="border border-gray-400 p-1">
                  <label className="block">
                    <span className="font-semibold">POSITION:</span>
                    <input
                      type="text"
                      value={formData.position}
                      onChange={(e) => setFormData({...formData, position: e.target.value})}
                      className="ml-1 w-32 border-b border-gray-400"
                    />
                  </label>
                </td>
                <td className="border border-gray-400 p-1">
                  <label className="block">
                    <span className="font-semibold">HOW LONG?</span>
                    <input
                      type="text"
                      value={formData.employmentLength}
                      onChange={(e) => setFormData({...formData, employmentLength: e.target.value})}
                      className="ml-1 w-16 border-b border-gray-400"
                      placeholder="Yrs/Mos"
                    />
                  </label>
                </td>
                <td className="border border-gray-400 p-1">
                  <label className="block">
                    <span className="font-semibold">PHONE:</span>
                    <input
                      type="tel"
                      value={formData.employerPhone}
                      onChange={(e) => setFormData({...formData, employerPhone: e.target.value})}
                      className="ml-1 w-24 border-b border-gray-400"
                    />
                  </label>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Income Section */}
        <div className="mt-4">
          <h3 className="font-bold text-xs mb-2">GROSS MONTHLY INCOME FROM EMPLOYMENT:</h3>
          <table className="w-full border-collapse text-xs">
            <tbody>
              <tr>
                <td className="border border-gray-400 p-1">
                  <label className="block">
                    <span className="font-semibold">$</span>
                    <input
                      type="number"
                      value={formData.grossMonthlyIncome}
                      onChange={(e) => setFormData({...formData, grossMonthlyIncome: e.target.value})}
                      className="ml-1 w-24 border-b border-gray-400"
                    />
                  </label>
                </td>
                <td className="border border-gray-400 p-1">
                  <label className="block">
                    <span className="font-semibold">ADDITIONAL MONTHLY INCOME: $</span>
                    <input
                      type="number"
                      value={formData.additionalIncome}
                      onChange={(e) => setFormData({...formData, additionalIncome: e.target.value})}
                      className="ml-1 w-24 border-b border-gray-400"
                    />
                  </label>
                </td>
                <td className="border border-gray-400 p-1">
                  <label className="block">
                    <span className="font-semibold">SOURCE:</span>
                    <input
                      type="text"
                      value={formData.additionalIncomeSource}
                      onChange={(e) => setFormData({...formData, additionalIncomeSource: e.target.value})}
                      className="ml-1 w-32 border-b border-gray-400"
                    />
                  </label>
                </td>
                <td className="border border-gray-400 p-1">
                  <label className="block">
                    <span className="font-semibold">TOTAL MONTHLY INCOME: $</span>
                    <input
                      type="number"
                      value={formData.totalMonthlyIncome}
                      onChange={(e) => setFormData({...formData, totalMonthlyIncome: e.target.value})}
                      className="ml-1 w-24 border-b border-gray-400"
                    />
                  </label>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* PART III - ASSET AND DEBT INFORMATION */}
      <div className="mb-4">
        <div className="bg-gray-800 text-white px-2 py-1 text-xs font-bold mb-2">
          PART III. ASSET AND DEBT INFORMATION - List ALL Debts Including Alimony, Child Support, Separate Maintenance
        </div>
        
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-400 p-1 text-left">TYPE OF CREDIT</th>
              <th className="border border-gray-400 p-1 text-left">COMPANY NAME</th>
              <th className="border border-gray-400 p-1 text-left">ADDRESS</th>
              <th className="border border-gray-400 p-1 text-left">ACCOUNT #</th>
              <th className="border border-gray-400 p-1 text-left">BALANCE</th>
              <th className="border border-gray-400 p-1 text-left">MONTHLY PAYMENT</th>
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3, 4].map((row) => (
              <tr key={row}>
                <td className="border border-gray-400 p-1">
                  <select className="w-full text-xs">
                    <option value="">Select...</option>
                    <option value="open">OPEN</option>
                    <option value="revolving">REVOLVING</option>
                    <option value="installment">INSTALLMENT</option>
                    <option value="closed">CLOSED</option>
                  </select>
                </td>
                <td className="border border-gray-400 p-1">
                  <input type="text" className="w-full border-b border-gray-400" />
                </td>
                <td className="border border-gray-400 p-1">
                  <input type="text" className="w-full border-b border-gray-400" />
                </td>
                <td className="border border-gray-400 p-1">
                  <input type="text" className="w-full border-b border-gray-400" />
                </td>
                <td className="border border-gray-400 p-1">
                  <input type="number" className="w-full border-b border-gray-400" placeholder="$" />
                </td>
                <td className="border border-gray-400 p-1">
                  <input type="number" className="w-full border-b border-gray-400" placeholder="$" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* INSURANCE Section */}
      <div className="mb-4">
        <div className="bg-gray-800 text-white px-2 py-1 text-xs font-bold mb-2">
          INSURANCE: Complete the following if you wish to apply for vehicle insurance in connection with this credit application:
        </div>
        
        <table className="w-full border-collapse text-xs">
          <tbody>
            <tr>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">INSURANCE COMPANY:</span>
                  <input
                    type="text"
                    value={formData.insuranceCompany}
                    onChange={(e) => setFormData({...formData, insuranceCompany: e.target.value})}
                    className="ml-1 w-48 border-b border-gray-400"
                  />
                </label>
              </td>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">AGENT:</span>
                  <input
                    type="text"
                    value={formData.insuranceAgent}
                    onChange={(e) => setFormData({...formData, insuranceAgent: e.target.value})}
                    className="ml-1 w-32 border-b border-gray-400"
                  />
                </label>
              </td>
              <td className="border border-gray-400 p-1">
                <label className="block">
                  <span className="font-semibold">PHONE:</span>
                  <input
                    type="tel"
                    value={formData.insurancePhone}
                    onChange={(e) => setFormData({...formData, insurancePhone: e.target.value})}
                    className="ml-1 w-24 border-b border-gray-400"
                  />
                </label>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Signature Section */}
      <div className="border-2 border-gray-400 p-4 mt-6">
        <p className="text-xs font-bold mb-2">PLEASE READ CAREFULLY AND SIGN BELOW.</p>
        <p className="text-xs mb-4">
          I understand and agree that if you extend credit to me or assist in the arranging of credit with any other party or parties, and to the extent permitted by law, you, your affiliates, agents and service providers, and your assignees, may monitor and record telephone calls regarding my account to assure the quality of your service or for other reasons.
        </p>
        <p className="text-xs mb-4 font-bold">
          WE ACKNOWLEDGE RECEIPT OF A COPY OF THIS APPLICATION.
        </p>
        
        <div className="grid grid-cols-2 gap-8 mt-8">
          <div>
            <div className="border-t border-gray-800 mt-8"></div>
            <p className="text-xs mt-1">APPLICANT'S SIGNATURE</p>
            <p className="text-xs">DATE: {formData.signatureDate}</p>
          </div>
          <div>
            <div className="border-t border-gray-800 mt-8"></div>
            <p className="text-xs mt-1">CO-APPLICANT'S SIGNATURE</p>
            <p className="text-xs">DATE: {formData.signatureDate}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditApplicationForm;
